import React from "react";
import { useTranslation } from "react-i18next";
import i18next from "src/i18n/config";
import { makeStyles } from "tss-react/mui";

import { PaneElement } from "@dashboard/shared/components/SidePanel/shared/PaneElement";
import { Group, License } from "@devices/types";
import { Paper, TableContainer, TableRow, Typography } from "@mui/material";
import { MinimalTable, MinimalTableCell, MinimalTableHeader } from "@shared/table/MinimalTableComponents";
import { TableBody } from "@shared/table/TableBody";
import { Header } from "@shared/table/TableHeader";
import { createColumnHelper, flexRender, getCoreRowModel, getSortedRowModel, useReactTable } from "@tanstack/react-table";

import { ActionCell } from "./ActionCell";

type TableData = {
    activationCode: string;
    groupId: string;
    universalGroupId: string;
    actions: License;
};

const columnHelper = createColumnHelper<TableData>();

const columns = [
    columnHelper.accessor("activationCode", {
        id: "activationCode",
        header: i18next.t("common:label.activationCode"),
        cell: (info) => (
            <MinimalTableCell>
                <PaneElement.CopyableValue value={info.getValue()} />
            </MinimalTableCell>
        ),
    }),
    columnHelper.accessor("groupId", {
        id: "groupId",
        header: i18next.t("sidepanels:groupPanel.table.header.location"),
        cell: (info) => (
            <MinimalTableCell>
                <Typography variant="body1">{info.getValue()}</Typography>
            </MinimalTableCell>
        ),
    }),
    columnHelper.accessor("universalGroupId", {
        id: "universalGroupId",
        header: i18next.t("sidepanels:groupPanel.table.header.target"),
        cell: (info) => (
            <MinimalTableCell>
                <Typography variant="body1">{info.getValue()}</Typography>
            </MinimalTableCell>
        ),
        enableSorting: false,
    }),
    columnHelper.accessor("actions", {
        id: "actions",
        header: "",
        cell: (info) => <ActionCell {...info.getValue()} />,
        enableSorting: false,
    }),
];

const getCombinedUniversalLicenses = (groupId: number, licenses: License[]): License[] => {
    const universalLicenses = licenses.filter((item) => item.universalGroupId);
    const universalLicensesInThisGroup = universalLicenses.filter((item) => item.groupId === groupId);
    const universalLicensesTargetingThisGroup = universalLicenses.filter((item) => item.universalGroupId === groupId);

    const combined = [...universalLicensesInThisGroup, ...universalLicensesTargetingThisGroup];
    const uniqueCombined = Array.from(new Map(combined.map((item) => [item.id, item])).values());

    return uniqueCombined;
};

const getTableData = (licenses: License[], groups: Group[]): TableData[] => {
    return licenses.map((item) => ({
        activationCode: item.activationCode,
        groupId: groups.find((group) => group.id === item.groupId)?.name || "Unknown",
        universalGroupId: groups.find((group) => group.id === item.universalGroupId)?.name || "Unknown",
        actions: item,
    }));
};

const useStyles = makeStyles()(() => ({
    table: { tableLayout: "auto", width: "100%" },
}));

type UniversalLicensesTableProps = {
    groupId: number;
    licenses: License[];
    groups: Group[];
};

export const UniversalLicensesTable = ({ groupId, licenses, groups }: UniversalLicensesTableProps) => {
    const { classes } = useStyles();
    const { t } = useTranslation(["sidepanels"]);

    const combinedUniversalLicenses = React.useMemo(() => getCombinedUniversalLicenses(groupId, licenses), [groupId, licenses]);
    const tableData = React.useMemo(() => getTableData(combinedUniversalLicenses, groups), [combinedUniversalLicenses, groups]);

    const table = useReactTable({
        data: tableData,
        columns,
        autoResetPageIndex: true,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
    });
    return (
        <TableContainer component={Paper}>
            <MinimalTable className={classes.table}>
                <MinimalTableHeader>
                    {table.getHeaderGroups().map((headerGroup) => (
                        <TableRow key={headerGroup.id}>
                            {headerGroup.headers.map((header) => (
                                <Header key={header.id} {...header} />
                            ))}
                        </TableRow>
                    ))}
                </MinimalTableHeader>
                <TableBody table={table} noDataMessage={t("sidepanels:groupPanel.table.noUniversalLicenses")}>
                    {table.getRowModel().rows.map((row) => (
                        <TableRow key={row.id}>
                            {row.getVisibleCells().map((cell) =>
                                flexRender(cell.column.columnDef.cell, {
                                    ...cell.getContext(),
                                    key: cell.id,
                                }),
                            )}
                        </TableRow>
                    ))}
                </TableBody>
            </MinimalTable>
        </TableContainer>
    );
};
